import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Search, Bike as BikeIcon, Pencil, Trash2, Layers, CheckSquare, X as XIcon, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = { numero: "", modello: "", taglia: "", numero_telaio: "", anno_produzione: "", categoria_noleggio: "", km_prossimo_tagliando: 0, note: "" };
const NO_CAT = "__none__";

export default function Inventory() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [bikes, setBikes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkCat, setBulkCat] = useState("");
  const [sedi, setSedi] = useState([]);
  const [sedeFilter, setSedeFilter] = useState("all");
  const [bulkSede, setBulkSede] = useState("");

  const load = async () => {
    const res = await api.get("/bikes");
    setBikes(res.data);
  };
  const loadCategories = async () => {
    try { const res = await api.get("/categories"); setCategories(res.data); } catch (e) {}
  };
  const loadSedi = async () => {
    try { const res = await api.get("/sedi"); setSedi(res.data); } catch (e) {}
  };
  useEffect(() => { load(); loadCategories(); loadSedi(); }, []);

  const catName = (id) => categories.find((c) => c.id === id)?.nome || null;
  const sedeName = (id) => sedi.find((s) => s.id === id)?.nome || null;

  useEffect(() => {
    if (params.get("nuova")) { openNew(); setParams({}); }
    // eslint-disable-next-line
  }, [params]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ numero: b.numero, modello: b.modello || "", taglia: b.taglia || "",
      numero_telaio: b.numero_telaio || "", anno_produzione: b.anno_produzione || "",
      categoria_noleggio: b.categoria_noleggio || "", km_prossimo_tagliando: b.km_prossimo_tagliando || 0, note: b.note || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.numero.trim()) { toast.error("Il numero bici è obbligatorio"); return; }
    setSaving(true);
    const payload = { ...form, km_prossimo_tagliando: parseInt(form.km_prossimo_tagliando) || 0 };
    try {
      if (editing) {
        await api.put(`/bikes/${editing.id}`, payload);
        toast.success("Bici aggiornata");
      } else {
        await api.post("/bikes", payload);
        toast.success("Bici creata");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const del = async (b) => {
    try {
      await api.delete(`/bikes/${b.id}`);
      toast.success("Bici eliminata");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const exitSelect = () => { setSelectMode(false); setSelected([]); setBulkCat(""); setBulkSede(""); };
  const applyBulkCategory = async () => {
    if (selected.length === 0) { toast.error("Seleziona almeno una bici"); return; }
    if (!bulkCat) { toast.error("Seleziona una categoria"); return; }
    try {
      const cat = bulkCat === NO_CAT ? "" : bulkCat;
      const res = await api.post("/bikes/bulk-category", { bike_ids: selected, categoria_noleggio: cat });
      toast.success(`Categoria assegnata a ${res.data.updated} bici`);
      exitSelect();
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const applyBulkSede = async () => {
    if (selected.length === 0) { toast.error("Seleziona almeno una bici"); return; }
    if (!bulkSede) { toast.error("Seleziona una sede"); return; }
    try {
      const sede = bulkSede === NO_CAT ? "" : bulkSede;
      const res = await api.post("/bikes/bulk-sede", { bike_ids: selected, sede_id: sede });
      toast.success(`Sede assegnata a ${res.data.moved} bici`);
      exitSelect();
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const filtered = bikes.filter((b) =>
    (sedeFilter === "all" || (sedeFilter === "none" ? !b.sede_attuale : b.sede_attuale === sedeFilter)) &&
    [b.numero, b.modello, b.numero_telaio].join(" ").toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Gestione bici</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">INVENTARIO</h1>
        </div>
        {hasPerm("inventory.create") && (
          <div className="flex gap-2">
            {hasPerm("inventory.edit") && (categories.length > 0 || sedi.length > 0) && !selectMode && (
              <button onClick={() => setSelectMode(true)} data-testid="select-mode-btn"
                className="flex items-center gap-2 rounded-sm border border-zinc-700 text-zinc-200 font-bold uppercase tracking-wide px-5 py-3 hover:bg-zinc-800 transition-colors">
                <CheckSquare className="h-5 w-5" /> Seleziona
              </button>
            )}
            <button onClick={openNew} data-testid="add-bike-btn"
              className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#d96a12] transition-colors active:scale-95">
              <Plus className="h-5 w-5" /> Nuova bici
            </button>
          </div>
        )}
      </div>

      {selectMode && (
        <div className="mb-6 bg-[#f98125]/10 border border-[#f98125]/40 rounded-sm p-3 flex items-center gap-3 flex-wrap" data-testid="bulk-bar">
          <span className="text-sm font-medium text-white">{selected.length} selezionate</span>
          <div className="flex-1 min-w-[200px] max-w-xs">
            <Select value={bulkCat} onValueChange={setBulkCat}>
              <SelectTrigger className="bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid="bulk-category-select"><SelectValue placeholder="Assegna categoria..." /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-zinc-800">
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                <SelectItem value={NO_CAT}>Nessuna categoria</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button onClick={applyBulkCategory} data-testid="apply-bulk-category"
            className="rounded-sm bg-[#f98125] text-white font-bold px-4 py-2 text-sm hover:bg-[#d96a12] transition-colors">Categoria</button>
          {sedi.length > 0 && (
            <>
              <div className="flex-1 min-w-[180px] max-w-xs">
                <Select value={bulkSede} onValueChange={setBulkSede}>
                  <SelectTrigger className="bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid="bulk-sede-select"><SelectValue placeholder="Assegna sede..." /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-zinc-800">
                    {sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                    <SelectItem value={NO_CAT}>Nessuna sede</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button onClick={applyBulkSede} data-testid="apply-bulk-sede"
                className="rounded-sm bg-[#f98125] text-white font-bold px-4 py-2 text-sm hover:bg-[#d96a12] transition-colors">Sede</button>
            </>
          )}
          <button onClick={exitSelect} data-testid="exit-select" className="rounded-sm border border-zinc-700 px-3 py-2 text-sm hover:bg-zinc-800 flex items-center gap-1"><XIcon className="h-4 w-4" /> Annulla</button>
        </div>
      )}

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
          <Input data-testid="inventory-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per numero, modello, telaio..." className="pl-9 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
        </div>
        {sedi.length > 0 && (
          <div className="w-52">
            <Select value={sedeFilter} onValueChange={setSedeFilter}>
              <SelectTrigger className="bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="inventory-sede-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-zinc-800">
                <SelectItem value="all">Tutte le sedi</SelectItem>
                {sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                <SelectItem value="none">Senza sede</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-zinc-600 text-sm py-16 text-center border border-dashed border-zinc-800 rounded-sm">
          Nessuna bici. Aggiungine una con "Nuova bici".
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((b) => (
            <div key={b.id} data-testid={`bike-card-${b.numero}`}
              className={`bg-[#141414] border rounded-sm p-4 transition-colors ${selectMode && selected.includes(b.id) ? "border-[#f98125]" : "border-zinc-800 hover:border-[#f98125]/50"}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 cursor-pointer"
                  onClick={() => selectMode ? toggleSelect(b.id) : navigate(`/bici/${b.id}`)}>
                  {selectMode && (
                    <Checkbox checked={selected.includes(b.id)} data-testid={`select-bike-${b.numero}`} className="mr-1" />
                  )}
                  <div className="h-12 w-12 bg-[#f98125]/15 rounded-sm flex items-center justify-center">
                    <span className="font-display text-2xl text-[#f98125]">{b.numero}</span>
                  </div>
                  <div>
                    <div className="font-semibold">{b.modello || "—"}</div>
                    <div className="text-xs text-zinc-500">Taglia {b.taglia || "—"} · {b.anno_produzione || "—"}</div>
                  </div>
                </div>
                {!selectMode && (
                  <div className="flex gap-1">
                    {hasPerm("inventory.edit") && (
                      <button onClick={() => openEdit(b)} data-testid={`edit-bike-${b.numero}`}
                        className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-sm"><Pencil className="h-4 w-4" /></button>
                    )}
                    {hasPerm("inventory.delete") && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`delete-bike-${b.numero}`}
                            className="p-1.5 text-zinc-500 hover:text-[#FF3B30] hover:bg-zinc-800 rounded-sm"><Trash2 className="h-4 w-4" /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#141414] border-zinc-800">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare la bici {b.numero}?</AlertDialogTitle>
                            <AlertDialogDescription>Verranno eliminati anche tutti gli interventi associati. Azione irreversibile.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del(b)} className="rounded-sm bg-[#FF3B30] hover:bg-[#cc2f26]">Elimina</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {catName(b.categoria_noleggio)
                  ? <span data-testid={`bike-cat-${b.numero}`} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider bg-[#f98125]/15 text-[#f98125] px-2 py-0.5 rounded-sm"><Layers className="h-3 w-3" /> {catName(b.categoria_noleggio)}</span>
                  : <span className="text-[11px] uppercase tracking-wider text-zinc-600">Categoria non assegnata</span>}
                {sedeName(b.sede_attuale) && (
                  <span data-testid={`bike-sede-${b.numero}`} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider bg-zinc-800/70 text-zinc-300 px-2 py-0.5 rounded-sm"><MapPin className="h-3 w-3" /> {sedeName(b.sede_attuale)}</span>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-zinc-800/60 flex items-center justify-between text-xs text-zinc-500">
                <span>Telaio: {b.numero_telaio || "—"}</span>
                <span className="text-[#f98125]">{b.maintenance_count || 0} interventi</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
              <BikeIcon className="h-5 w-5 text-[#f98125]" /> {editing ? "Modifica bici" : "Nuova bici"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-1">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Numero bici *</Label>
              <Input data-testid="bike-form-numero" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })}
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. 12" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Modello</Label>
              <Input data-testid="bike-form-modello" value={form.modello} onChange={(e) => setForm({ ...form, modello: e.target.value })}
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. Trek Rail 9" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Taglia</Label>
              <Input data-testid="bike-form-taglia" value={form.taglia} onChange={(e) => setForm({ ...form, taglia: e.target.value })}
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. M / L" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Anno produzione</Label>
              <Input data-testid="bike-form-anno" value={form.anno_produzione} onChange={(e) => setForm({ ...form, anno_produzione: e.target.value })}
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. 2024" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Categoria di noleggio</Label>
              <Select value={form.categoria_noleggio || NO_CAT} onValueChange={(v) => setForm({ ...form, categoria_noleggio: v === NO_CAT ? "" : v })}>
                <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="bike-form-categoria"><SelectValue placeholder="Nessuna categoria" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-zinc-800">
                  <SelectItem value={NO_CAT}>Nessuna categoria</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Numero di telaio</Label>
              <Input data-testid="bike-form-telaio" value={form.numero_telaio} onChange={(e) => setForm({ ...form, numero_telaio: e.target.value })}
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. WTU123456" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Soglia km prossimo tagliando</Label>
              <Input data-testid="bike-form-tagliando" type="number" value={form.km_prossimo_tagliando || 0} onChange={(e) => setForm({ ...form, km_prossimo_tagliando: e.target.value })}
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. 1000 (0 = nessun avviso)" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Note</Label>
              <Input data-testid="bike-form-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
            <button onClick={save} disabled={saving} data-testid="bike-form-save"
              className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors disabled:opacity-50">
              {saving ? "Salvataggio..." : "Salva"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
